import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getReferenceCacheStaleness } from "./db";
import {
  invalidatePosReferenceQueries,
  referenceCacheManifestKey,
  refreshPosReferenceCache,
} from "./reference-cache";
import type { ReferenceCacheManifest } from "./types";

const DEFAULT_MAX_AGE_MS = 12 * 60 * 60 * 1000;

type ReferenceCacheRefreshState = {
  refreshing: boolean;
  lastManifest: ReferenceCacheManifest | null;
  error: string | null;
};

export function useReferenceCacheRefresh(
  branchId: string | null | undefined,
  deviceId: string | null | undefined,
  options: {
    maxAgeMs?: number;
    includeVariantDetails?: boolean;
    enabled?: boolean;
  } = {},
): ReferenceCacheRefreshState {
  const [state, setState] = useState<ReferenceCacheRefreshState>({
    refreshing: false,
    lastManifest: null,
    error: null,
  });
  const queryClient = useQueryClient();
  const includeVariantDetails = options.includeVariantDetails ?? false;
  const maxAgeMs = options.maxAgeMs ?? DEFAULT_MAX_AGE_MS;
  const enabled = options.enabled ?? true;

  useEffect(() => {
    if (!enabled || !branchId || !deviceId) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) return;

    const selectedBranchId = branchId;
    const selectedDeviceId = deviceId;
    let cancelled = false;

    async function refreshIfStale() {
      const staleness = await getReferenceCacheStaleness(
        referenceCacheManifestKey(selectedBranchId),
        maxAgeMs,
      );
      if (!staleness.stale) return;

      setState((prev) => ({ ...prev, refreshing: true, error: null }));
      try {
        const manifest = await refreshPosReferenceCache({
          branchId: selectedBranchId,
          deviceId: selectedDeviceId,
          maxAgeMs,
          includeVariantDetails,
        });
        if (!cancelled) {
          setState({ refreshing: false, lastManifest: manifest, error: null });
          await invalidatePosReferenceQueries(queryClient);
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            refreshing: false,
            lastManifest: null,
            error:
              error instanceof Error
                ? error.message
                : "Failed to refresh POS reference cache",
          });
        }
      }
    }

    void refreshIfStale();

    return () => {
      cancelled = true;
    };
  }, [
    branchId,
    deviceId,
    enabled,
    includeVariantDetails,
    maxAgeMs,
    queryClient,
  ]);

  return state;
}
